import { useCollection } from '@amzn/awsui-collection-hooks';
import {
    Box,
    Button,
    DateRangePicker,
    DateRangePickerProps,
    Header,
    Input,
    Pagination,
    PropertyFilter,
    SpaceBetween,
    Table,
} from '@amzn/awsui-components-react';
import { get } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useFlyout } from '../../../../hooks/useFlyout';
import { CommonDomainProps } from '../../../../models/types';
import {
    getFilterCounterText,
    Preferences,
} from '../../../../utils/tablePreferences';
import { PROPERTY_FILTERING_I18N_CONSTANTS } from '../../../../utils/constants';
import {
    columns,
    DEFAULT_PREFERENCES,
    FILTERING_PROPERTIES,
    i18nStrings,
    PAGE_SIZE_OPTIONS,
    RELATIVE_OPTIONS,
    VISIBLE_CONTENT_OPTIONS,
} from '../../../../pages/DomainDetails/TMSIssues/constants';
import { useJunoTmsQueryArchive } from "./hooks/useJunoTMSQueryArchive";
import { TmsDetails } from '../../../../pages/DomainDetails/TMSIssues/TmsDetails';
import { isError } from "../../../../ErrorHandler/apiErrorHandler";
import { ErrorMessageComponent } from "../../../../ErrorHandler/errorMessageComponent";
import { ApiList }from "../../../../ErrorHandler/utils";
import { errorBanner } from '../../../../ErrorHandler/errorBanner';

const JunoTMSIssuesArchive = (props: CommonDomainProps) => {
    const setFlyout = useFlyout();
    const [errorMessage, setErrorMessage] = useState(null);
    const [currentDate, setCurrentDate] = React.useState<any>({
        type: 'relative',
        amount: 1,
        unit: 'week',
    });

    const [valuePrefix, setValuePrefix] = React.useState('');

    const domainIdentifier = props.domainIdentifier;

    var [getTMSQueryArchive, { loading, data: domain, error }] =
        useJunoTmsQueryArchive();

    const isErrorPresent = isError(error)
    useEffect(() => {
        if (isErrorPresent) {
            setErrorMessage(errorBanner('Unable to get archived TMS issue.', error));
        } else {
            // Query is healthy: drop any banner left over from an earlier failure.
            setErrorMessage(null);
        }
    }, [isErrorPresent, error]);


    useEffect(() => {
        var firstday,lastday;
        firstday = moment(Date.now() - 7 * 24 * 60 * 60 * 1000);
        lastday = moment(new Date());

        const startTimestamp = Math.floor(new Date(firstday).getTime() ).toString();
        const endTimestamp = Math.floor(new Date(lastday).getTime() ).toString();
        getTMSQueryArchive({
            variables: {
                collectionIdentifier: domainIdentifier,
                startTime: startTimestamp,
                endTime: endTimestamp,
                issuePrefix: valuePrefix,
            },
        });
    }, [domainIdentifier, getTMSQueryArchive, valuePrefix]);

    const handlePrefixChange = () => {
        var startMoment, endMoment;

        if ('absolute' === currentDate.type) {
            startMoment = moment(currentDate.startDate);
            endMoment = moment(currentDate.endDate);
        } else if ('relative' === currentDate.type) {
            startMoment = moment().subtract(currentDate.amount, currentDate.unit);
            endMoment = moment();
        }
        const startTimestamp = Math.floor(new Date(startMoment).getTime() );
        const endTimestamp = Math.floor(new Date(endMoment).getTime() );

        getTMSQueryArchive({
            variables: {
                collectionIdentifier: domainIdentifier,
                startTime: startTimestamp.toString(),
                endTime: endTimestamp.toString(),
                issuePrefix: valuePrefix,
            },
        });
    };

    const handleDateChange = ({ detail }) => {
        var startMoment, endMoment;

        if ('absolute' === detail.value.type) {
            startMoment = moment(detail.value.startDate);
            endMoment = moment(detail.value.endDate);
        } else if ('relative' === detail.value.type) {
            startMoment = moment().subtract(detail.value.amount, detail.value.unit);
            endMoment = moment();
        }
        const startTimestamp = Math.floor(new Date(startMoment).getTime() );
        const endTimestamp = Math.floor(new Date(endMoment).getTime() );
        getTMSQueryArchive({
            variables: {
                collectionIdentifier: domainIdentifier,
                startTime: startTimestamp.toString(),
                endTime: endTimestamp.toString(),
                issuePrefix: valuePrefix,
            },
        });

        setCurrentDate(detail.value);
    };

    const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

    const {
        items,
        actions,
        filteredItemsCount,
        collectionProps,
        propertyFilterProps,
        paginationProps,
    } = useCollection(domain ? get(domain, 'getJunoTmsIssuesArchive', []) : [], {
        propertyFiltering: {
            filteringProperties: FILTERING_PROPERTIES,
            empty: (
                <EmptyState
                    title="No TMS issues"
                    subtitle="No TMS Archive issues to display."
                    action={<span></span>}
                />
            ),
            noMatch: (
                <EmptyState
                    title="No matches"
                    subtitle="We can’t find a match."
                    action={
                        <Button onClick={() => actions.setFiltering('')}>
                            Clear filter
                        </Button>
                    }
                />
            ),
        },

        pagination: { pageSize: preferences.pageSize },
        sorting: {},
    });

    function EmptyState({ title, subtitle, action }) {
        return (
            <Box textAlign="center" color="inherit">
                <Box variant="strong" textAlign="center" color="inherit">
                    {title}
                </Box>
                <Box variant="p" padding={{ bottom: 's' }} color="inherit">
                    {subtitle}
                </Box>
                {action}
            </Box>
        );
    }

    return (
        <div>
            {errorMessage ? (
                <ErrorMessageComponent errorMessage={errorMessage} apiName={ApiList.TMS_ISSUES_ARCHIVE}/>
            ) : (
                <Table
                    {...collectionProps}
                    header={
                        <div>
                            <Header
                                actions={
                                    <SpaceBetween direction="horizontal" size="s">
                                        <label>Search by Prefix:</label>
                                        <Input
                                            onChange={({ detail }) => setValuePrefix(detail.value)}
                                            value={valuePrefix}
                                        />
                                        <DateRangePicker
                                            onChange={handleDateChange}
                                            value={currentDate}
                                            relativeOptions={RELATIVE_OPTIONS}
                                            i18nStrings={i18nStrings}
                                            placeholder="Filter by a date and time range"
                                        />
                                        <Button variant="primary" onClick={handlePrefixChange}>
                                            Fetch Issues
                                        </Button>
                                    </SpaceBetween>
                                }
                            ></Header>
                        </div>
                    }
                    columnDefinitions={[
                        {
                            id: 'issueType',
                            sortingField: 'issueType',
                            name: 'Issue type',
                            header: 'Issue type',
                            cell: (e) => (
                                <Button
                                    variant="link"
                                    onClick={() =>
                                        setFlyout({
                                            title: e.issueType,
                                            size: 'm',
                                            content: <TmsDetails description={e.details} />,
                                        })
                                    }
                                >
                                    <span style={{ color: '#00a1c9' }}>{e.issueType}</span>
                                </Button>
                            ),
                        },
                        ...columns,
                    ]}
                    stickyHeader={true}
                    resizableColumns={true}
                    wrapLines={preferences.wraplines}
                    loading={loading}
                    loadingText={
                        loading ? 'Loading TMS Archive issues...' : 'No TMS issues found.'
                    }
                    visibleColumns={preferences.visibleContent}
                    items={items}
                    pagination={
                        <Pagination
                            {...paginationProps}
                            ariaLabels={{
                                nextPageLabel: 'Next page',
                                previousPageLabel: 'Previous page',
                                pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`,
                            }}
                        />
                    }
                    filter={
                        <PropertyFilter
                            {...propertyFilterProps}
                            i18nStrings={PROPERTY_FILTERING_I18N_CONSTANTS}
                            countText={getFilterCounterText(filteredItemsCount)}
                        />
                    }
                    preferences={
                        <Preferences
                            preferences={preferences}
                            setPreferences={setPreferences}
                            disabled={false}
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                            visibleContentOptions={VISIBLE_CONTENT_OPTIONS}
                        />
                    }
                />
            )}
        </div>
    );
};

export { JunoTMSIssuesArchive };
